import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useCategories, useTags, useArchive, useRecentPosts } from '../../hooks/blog';

interface BlogSidebarProps {
  domain: string;
  onSearch: (query: string) => void;
  onCategorySelect: (id: number) => void;
  onTagSelect: (tag: string) => void;
  onArchiveSelect: (year: number, month: number) => void;
}

const BlogSidebar: React.FC<BlogSidebarProps> = ({
  domain,
  onSearch,
  onCategorySelect,
  onTagSelect,
  onArchiveSelect,
}) => {
  const [search, setSearch] = useState('');
  const { categories, loading: catLoading } = useCategories(domain);
  const { tags, loading: tagLoading } = useTags(domain);
  const { archive, loading: archLoading } = useArchive(domain);
  const { posts: recentPosts, loading: recentLoading } = useRecentPosts(5, domain);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    onSearch(value);
  };

  return (
    <div className="space-y-8">
      {/* Поиск */}
      <div className="relative">
        <input
          type="text"
          placeholder="Поиск по статьям..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burgundy focus:border-transparent"
          value={search}
          onChange={handleSearchChange}
        />
        <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
      </div>

      {/* Категории */}
      <div>
        <h3 className="text-lg font-bold text-burgundy mb-4">Категории</h3>
        {catLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {categories.map(cat => (
              <li key={cat.id}>
                <button
                  onClick={() => onCategorySelect(cat.id)}
                  className="text-gray-700 hover:text-burgundy hover:underline"
                >
                  {cat.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Теги */}
      <div>
        <h3 className="text-lg font-bold text-burgundy mb-4">Теги</h3>
        {tagLoading ? (
          <div className="flex flex-wrap gap-2">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-6 w-16 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map(tag => (
              <button
                key={tag}
                onClick={() => onTagSelect(tag)}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-burgundy hover:text-white transition"
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Архив */}
      <div>
        <h3 className="text-lg font-bold text-burgundy mb-4">Архив</h3>
        {archLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {archive.map(entry => (
              <li key={`${entry.year}-${entry.month}`}>
                <button
                  onClick={() => onArchiveSelect(entry.year, entry.month)}
                  className="text-gray-700 hover:text-burgundy hover:underline"
                >
                  {entry.monthName} {entry.year} ({entry.count})
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Последние посты */}
      <div>
        <h3 className="text-lg font-bold text-burgundy mb-4">Последние статьи</h3>
        {recentLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <ul className="space-y-4">
            {recentPosts.map(post => (
              <li key={post.id} className="border-b pb-4 last:border-0">
                <a
                  href={`/blog/${post.slug}`}
                  className="text-gray-800 hover:text-burgundy hover:underline font-medium"
                >
                  {post.title}
                </a>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(post.publish_date).toLocaleDateString('ru-RU')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BlogSidebar;